import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import moment from 'moment';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import IconButton from '@mui/material/IconButton';
import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import {
  ButtonWrapper,
  CancelButton,
  DialogHeader,
  DialogHeading,
  SaveButton,
} from '../../../../../../Episode/Episode.Styled';
import FormInputText from '../../../../../../common/Forms/FormTextField';
import FormSuccess from '../../../../../../common/Forms/FormSuccess';
import DropdownFormInput from '../../../../../../common/Forms/DropdownForm';
import {
  setReferenceNumber,
  setComplianceCategoryId,
  setComplianceDescriptionId,
  setStartDate,
  setEndDate,
  setDescription,
} from '../../../../../../../redux/organization/organizationTabs/organizationCompliance/organizationComplianceList/organizationComplianceDataSlice';
import {
  formDateFormat,
  ORGANIZATION_COMPLIANCE_ASSOCIATED_UPDATE_URL,
  ORGANIZATION_COMPLIANCE_UPDATE_URL,
} from '../../../../../../../constants';
import {
  postStopApi,
  setStopApi,
} from '../../../../../../../services/StopApi/StopApi';
import { getOrganizationComplianceForm } from '../../../../../../../redux/organization/organizationTabs/organizationCompliance/organizationComplianceSlice';
import { organizationComplianceListFormValidation } from '../../../../../OrganizationUtils/organizationValidation';

const ComplianceDialog = (props) => {
  const { open, setOpen, openBackdrop, authToken } = props;

  const dispatch = useDispatch();
  const [formSuccess, setFormSuccess] = useState(false);
  const [
    isNewOrganizationComplianceListForm,
    setIsNewOrganizationComplianceListForm,
  ] = useState(false);

  const organizationComplianceListForm = useSelector(
    (state) => state.organizationComplianceListForm
  );

  const organizationReferenceData = useSelector(
    (state) => state.organizationReferenceData
  );
  const { complianceCategoryList } = organizationReferenceData;

  const {
    cplId,
    ReferenceNumber,
    complianceCategoryId,
    complianceDescriptionId,
    cplOrgId,
    description,
    startDate,
    endDate,
  } = organizationComplianceListForm;

  const fetchData = async () => {
    const commonParams = `${ORGANIZATION_COMPLIANCE_UPDATE_URL}/${cplOrgId}`;
    const authorization = authToken;
    dispatch(getOrganizationComplianceForm({ commonParams, authorization }));
  };

  const defaultValue = {
    cplId: cplId,
    ReferenceNumber: ReferenceNumber,
    complianceCategoryId: complianceCategoryId ? complianceCategoryId : '',
    complianceDescriptionId: complianceDescriptionId
      ? complianceDescriptionId
      : '',
    cplOrgId: cplOrgId,
    description: description,
    startDate: moment(startDate).format(formDateFormat),
    endDate: moment(endDate).format(formDateFormat),
  };

  const useFunction = useForm({
    mode: 'onChange',
    defaultValues: defaultValue,
    resolver: yupResolver(organizationComplianceListFormValidation),
  });
  const { handleSubmit, reset, control, setValue, watch, register, formState } =
    useFunction;
  const { isValid, dirtyFields, errors } = formState;

  useEffect(() => {
    reset(defaultValue);
  }, [
    defaultValue.cplId,
    defaultValue.ReferenceNumber,
    defaultValue.complianceCategoryId,
    defaultValue.complianceDescriptionId,
    defaultValue.cplOrgId,
    defaultValue.description,
    defaultValue.startDate,
    defaultValue.endDate,
  ]);

  const handleClose = () => {
    setOpen(false);
    reset(defaultValue);
  };

  const onSubmit = (data) => {
    const url = `${ORGANIZATION_COMPLIANCE_ASSOCIATED_UPDATE_URL}`;
    const authorization = authToken;
    if (
      organizationComplianceListForm?.newOrganizationComplianceListForm === true
    ) {
      const payload = {
        cplId: 0,
        cplOrgId: cplOrgId,
        ReferenceNumber: ReferenceNumber,
        complianceCategory: complianceCategoryId,
        description: description,
        startDate:
          moment(startDate).format(formDateFormat) === 'Invalid date'
            ? null
            : moment(startDate).format(formDateFormat),
        endDate:
          moment(endDate).format(formDateFormat) === 'Invalid date'
            ? null
            : moment(endDate).format(formDateFormat),
      };
      postStopApi({ url, payload, authorization }).then((response) => {
        if (response.succeeded === true) {
          fetchData();
          setFormSuccess(true);
          setIsNewOrganizationComplianceListForm(true);
          setOpen(false);
        }
      }).catch((e) => alert(e));
    } else {
      const payload = {
        cplId: cplId,
        cplOrgId: cplOrgId,
        ReferenceNumber: ReferenceNumber,
        complianceCategory: complianceCategoryId,
        description: description,
        startDate:
          moment(startDate).format(formDateFormat) === 'Invalid date'
            ? null
            : moment(startDate).format(formDateFormat),
        endDate:
          moment(endDate).format(formDateFormat) === 'Invalid date'
            ? null
            : moment(endDate).format(formDateFormat),
      };
      setStopApi({ url, payload, authorization }).then((response) => {
        if (response.succeeded === true) {
          fetchData();
          setFormSuccess(true);
          setIsNewOrganizationComplianceListForm(false);
          setOpen(false);
        }
      }).catch((e) => alert(e));
    }
  };

  return (
    <>
      <Dialog
        open={open}
        PaperProps={{
          sx: {
            maxWidth: 'unset',
            width: '800px',
            maxHeight: 'unset',
          },
        }}
      >
        {openBackdrop ? (
          <Backdrop
            sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={openBackdrop}
          >
            <CircularProgress />
          </Backdrop>
        ) : (
          <>
            <DialogHeader>
              <DialogHeading variant='h5'>Compliance ID :</DialogHeading>
              <IconButton onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </DialogHeader>
            <Box style={{ padding: '10px 40px' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormInputText
                    name='ReferenceNumber'
                    type='number'
                    control={control}
                    errors={!!errors.ReferenceNumber}
                    helperText={errors?.ReferenceNumber?.message}
                    label='Refrence Number'
                    register={register('ReferenceNumber', {
                      onChange: (e) =>
                        dispatch(setReferenceNumber(e.target.value)),
                    })}
                  />
                  <DropdownFormInput
                    name='complianceCategoryId'
                    control={control}
                    options={complianceCategoryList}
                    complianceCategory={true}
                    defaultId={defaultValue?.complianceCategoryId}
                    label='Compliance Category'
                    register={register('complianceCategoryId', {
                      onChange: (e) =>
                        dispatch(setComplianceCategoryId(e.target.value)),
                    })}
                  />
                  <DropdownFormInput
                    name='complianceDescriptionId'
                    control={control}
                    options={complianceCategoryList}
                    complianceDescription={true}
                    defaultId={defaultValue?.complianceDescriptionId}
                    label='Compliance Description'
                    register={register('complianceDescriptionId', {
                      onChange: (e) =>
                        dispatch(setComplianceDescriptionId(e.target.value)),
                    })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormInputText
                    name='startDate'
                    type='date'
                    control={control}
                    shrink={true}
                    label='Start Date'
                    register={register('startDate', {
                      onChange: (e) => dispatch(setStartDate(e.target.value)),
                    })}
                  />
                  <FormInputText
                    name='endDate'
                    type='date'
                    control={control}
                    shrink={true}
                    minValue={defaultValue?.startDate}
                    label='End Date'
                    register={register('endDate', {
                      onChange: (e) => dispatch(setEndDate(e.target.value)),
                    })}
                  />
                  <FormInputText
                    name='description'
                    type='text'
                    control={control}
                    errors={!!errors.description}
                    helperText={errors?.description?.message}
                    label='Description'
                    register={register('description', {
                      onChange: (e) => dispatch(setDescription(e.target.value)),
                    })}
                  />
                </Grid>
              </Grid>
              <ButtonWrapper>
                <CancelButton
                  variant='contained'
                  //disabled={_.isEmpty(dirtyFields) || !isValid} {defaultValue?.authId}
                  onClick={handleClose}
                >
                  Cancel
                </CancelButton>
                <SaveButton
                  variant='contained'
                  //disabled={_.isEmpty(dirtyFields) || !isValid}
                  onClick={handleSubmit(onSubmit)}
                >
                  Save
                </SaveButton>
              </ButtonWrapper>
            </Box>
          </>
        )}
      </Dialog>
      <FormSuccess
        formSuccess={formSuccess}
        setFormSuccess={setFormSuccess}
        isNewForm={isNewOrganizationComplianceListForm}
      />
    </>
  );
};

export default ComplianceDialog;
